import {
  ComplianceCheckStatusReportEnum,
  IComplianceChecksElementary,
  IComplianceReport,
} from "@shared/lib/types/drafting";
import { AlertCircle, Check, Loader2, RefreshCw, X } from "lucide-react";
import styles from "./styles.module.scss";

type ScriptComplianceReportProps = {
  report?: IComplianceReport;
  status?: string;
  error?: string | null;
  outdated: boolean;
  isRunning: boolean;
  rerunDisabled?: boolean;
  onRerun?: () => void;
};

const GROUPS: { title: string; key: keyof IComplianceReport }[] = [
  { title: "Brand guardrails", key: "brandGuardRailsChecks" },
  { title: "Advanced prompting", key: "advancedPromptingChecks" },
  { title: "Performance", key: "performanceGuidelinesChecks" },
  { title: "Consistency", key: "consistencyChecks" },
  { title: "Safety", key: "safetyChecks" },
  { title: "Script vs draft", key: "scriptVsDraftingConsistencyChecks" },
];

function statusClass(status?: string) {
  if (status === ComplianceCheckStatusReportEnum.Compliant)
    return styles.statusPass;
  if (status === ComplianceCheckStatusReportEnum.PartiallyCompliant) {
    return styles.statusWarn;
  }
  if (status === ComplianceCheckStatusReportEnum.NonCompliant)
    return styles.statusFail;
  return styles.statusNeutral;
}

function CheckRow({ check }: { check: IComplianceChecksElementary }) {
  const icon =
    check.status === ComplianceCheckStatusReportEnum.Compliant ? (
      <Check size={12} />
    ) : check.status === ComplianceCheckStatusReportEnum.PartiallyCompliant ? (
      <AlertCircle size={12} />
    ) : (
      <X size={12} />
    );

  return (
    <li className={styles.checkRow}>
      <span className={`${styles.checkIcon} ${statusClass(check.status)}`}>
        {icon}
      </span>
      <div>
        <p className={styles.checkPoint}>{check.point}</p>
        {check.reason ? (
          <p className={styles.checkReason}>{check.reason}</p>
        ) : null}
      </div>
    </li>
  );
}

export function ScriptComplianceReport({
  report,
  status,
  error,
  outdated,
  isRunning,
  rerunDisabled = false,
  onRerun,
}: ScriptComplianceReportProps) {
  const running = isRunning || status === "Pending";
  if (!report && !running && !error) return null;

  return (
    <section className={styles.report}>
      <div className={styles.reportHeader}>
        <h3 className={styles.reportTitle}>Compliance report</h3>
        {outdated ? (
          <span className={styles.outdatedBadge}>Outdated</span>
        ) : null}
      </div>
      {outdated ? (
        <div className={styles.outdatedBanner}>
          <p className={styles.outdatedCopy}>
            Request prompt changed. This compliance review is outdated. Re-run
            the script check to update it.
          </p>
          <button
            type="button"
            className={styles.secondaryBtn}
            disabled={rerunDisabled || running}
            onClick={onRerun}
          >
            <RefreshCw size={13} />
            Re-run script check
          </button>
        </div>
      ) : null}
      <div className={outdated ? styles.reportOutdated : undefined}>
        {running ? (
          <p className={styles.analyzing}>
            <Loader2 size={14} className="spin-animation" />
            Checking this script…
          </p>
        ) : null}
        {error && status === "Failed" ? (
          <p className={styles.reportError}>{error}</p>
        ) : null}
        {report ? (
          <>
            <div className={styles.reportSummary}>
              <span
                className={`${styles.statusPill} ${statusClass(report.status)}`}
              >
                {report.status || "Checked"}
              </span>
              <span className={styles.score}>Score {report.score}</span>
            </div>
            {GROUPS.map((group) => {
              const items = report[group.key];
              if (!Array.isArray(items) || items.length === 0) return null;
              return (
                <div key={group.title} className={styles.checkGroup}>
                  <h4 className={styles.checkGroupTitle}>{group.title}</h4>
                  <ul className={styles.checkList}>
                    {items.map((check, index) => (
                      <CheckRow key={`${group.title}-${index}`} check={check} />
                    ))}
                  </ul>
                </div>
              );
            })}
          </>
        ) : null}
      </div>
    </section>
  );
}
